import { useMemo, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router';
import { Sidebar, Topbar, type NavGroup } from '@/components';
import { useAuthStore } from '@/stores/auth';
import { useNotificationStore } from '@/stores/notification';
import { useApi } from '@/api/hooks';
import styles from './ConsoleLayout.module.css';

interface BillingItem { status: string; }
interface Ticket { status: string; }
interface LeadItem { status: string; }
interface ContactItem { is_read: boolean; }

// ─── ロール別メニュー制御定義 ─────────────────────────
// super_admin: 全メニュー表示
// admin:       日常運用（テナント管理・課金・サポート・リード）+ 閲覧系
// viewer:      閲覧のみ（ダッシュボード・モニタリング・一覧表示）
const ROLE_MENU_ACCESS: Record<string, Set<string>> = {
  super_admin: new Set([
    'dashboard', 'tenants', 'leads', 'contacts', 'plans', 'billing',
    'monitoring', 'certificates', 'support',
    'master', 'users', 'legal', 'templates', 'excel-templates', 'profile',
  ]),
  admin: new Set([
    'dashboard', 'tenants', 'leads', 'contacts', 'plans', 'billing',
    'monitoring', 'support', 'legal', 'templates', 'excel-templates', 'profile',
  ]),
  viewer: new Set([
    'dashboard', 'tenants', 'monitoring', 'support', 'profile',
  ]),
};

function canAccess(role: string | undefined, menuId: string): boolean {
  const allowed = ROLE_MENU_ACCESS[role ?? ''];
  if (!allowed) return false;
  return allowed.has(menuId);
}

function useDynamicNav(role: string | undefined): NavGroup[] {
  const { data: billing } = useApi<BillingItem[]>('/console/billing');
  const { data: tickets } = useApi<Ticket[]>('/console/tickets');
  const { data: leadsData } = useApi<LeadItem[]>('/console/leads');
  const { data: contactsData } = useApi<ContactItem[]>('/console/contacts');

  const unpaidCount = billing?.filter((b) => b.status === 'unpaid').length ?? 0;
  const openTicketCount = tickets?.filter((t) => t.status === 'open' || t.status === 'in_progress').length ?? 0;
  const newLeadsCount = leadsData?.filter((l) => l.status === 'new').length ?? 0;
  const unreadContacts = contactsData?.filter((c) => !c.is_read).length ?? 0;

  return useMemo(() => {
    const allGroups: NavGroup[] = [
      { label: 'Overview', items: [{ id: 'dashboard', label: 'ダッシュボード', icon: '📊' }] },
      { label: 'テナント管理', items: [
        { id: 'tenants', label: 'テナント一覧', icon: '🏢' },
        { id: 'leads', label: 'リード管理', icon: '📋', badge: newLeadsCount },
        { id: 'contacts', label: '問い合わせ', icon: '📩', badge: unreadContacts },
        { id: 'plans', label: 'プラン管理', icon: '💎' },
        { id: 'billing', label: '課金・請求', icon: '💳', badge: unpaidCount },
      ]},
      { label: '監視・運用', items: [
        { id: 'monitoring', label: '利用状況監視', icon: '📡' },
        { id: 'certificates', label: 'SSL証明書', icon: '🔒' },
        { id: 'support', label: 'サポート', icon: '🎧', badge: openTicketCount },
      ]},
      { label: 'システム', items: [
        { id: 'master', label: 'マスタ設定', icon: '⚙️' },
        { id: 'users', label: 'ユーザー管理', icon: '👥' },
        { id: 'templates', label: 'テンプレート管理', icon: '📝' },
        { id: 'excel-templates', label: 'Excel帳票管理', icon: '📊' },
        { id: 'legal', label: '法的ページ管理', icon: '📄' },
        { id: 'profile', label: 'プロフィール設定', icon: '👤' },
      ]},
    ];

    // ロール別フィルタ: アクセス権のないメニューを除外
    return allGroups
      .map(g => ({
        ...g,
        items: g.items.filter(item => canAccess(role, item.id)),
      }))
      .filter(g => g.items.length > 0);
  }, [role, unpaidCount, openTicketCount, newLeadsCount, unreadContacts]);
}

const pageTitles: Record<string, string> = {
  dashboard: 'ダッシュボード', tenants: 'テナント一覧', plans: 'プラン管理',
  billing: '課金・請求', monitoring: '利用状況監視', certificates: 'SSL証明書管理',
  support: 'サポート', master: 'マスタ設定', leads: 'リード管理', contacts: '問い合わせ管理',
  users: 'ユーザー管理', templates: 'テンプレート管理', 'excel-templates': 'Excel帳票管理', legal: '法的ページ管理', profile: 'プロフィール設定',
};

export function ConsoleShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const navGroups = useDynamicNav(user?.role);
  const fetchNotifications = useNotificationStore((s) => s.fetch);

  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, 30_000);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  const activeId = location.pathname.split('/').pop() || 'dashboard';

  const handleNavigate = (id: string) => navigate(`/${id}`);
  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className={styles.app}>
      <Sidebar
        groups={navGroups}
        activeId={activeId}
        onNavigate={handleNavigate}
        userName={user?.name}
        userRole={user?.role}
        userEmail={user?.email}
        onLogout={handleLogout}
      />
      <div className={styles.main}>
        <Topbar title={pageTitles[activeId] ?? ''} />
        <div className={styles.content}><Outlet /></div>
      </div>
    </div>
  );
}

// ロール判定ヘルパー（各ページで使用）
export { canAccess, ROLE_MENU_ACCESS };
